import * as React from "react";
import { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { BusinessReportsOutcomeListRowStatus } from "@/modules/snapshots/outcomeListRowStatus";
import { CircleLoader } from "@/components/ui/circleLoader";
import colors from "tailwindcss/colors";
import { SnapshotCollectionsOutcomesRowVerified } from "./rowVerified";
import { SnapshotCollectionsOutcomesRowActions } from "./rowActions";
import { SnapshotCheckbox, type SnapshotCheckboxChangeMeta } from "@/components/snapshots/SnapshotCheckbox";

interface Props {
  businessReportOutcome: BusinessReportOutcome;
  selectedIds?: Set<string>;
  toggleSelection?: (id: string, checked: boolean, meta: SnapshotCheckboxChangeMeta) => void;
}

export const SnapshotCollectionsOutcomesRow: React.FC<Props> = ({
  businessReportOutcome,
  selectedIds,
  toggleSelection,
}) => {
  const normalizedProgressbar = parseInt(String((businessReportOutcome.snapshot_progress || 0) * 100), 10);
  const { formatDateShort, formatTime, formatDateTime } = useDateFormatter();

  return (
    <tr>
      <td className="px-3 py-2">
        {selectedIds && toggleSelection && (
          <SnapshotCheckbox
            checked={selectedIds.has(businessReportOutcome.id)}
            onCheckedChange={(checked, meta) => toggleSelection(businessReportOutcome.id, checked, meta)}
            ariaLabel={`Select ${businessReportOutcome.name ?? businessReportOutcome.id}`}
          />
        )}
      </td>
      <td className="py-4 pl-4 pr-3 text-sm sm:pl-6" title={businessReportOutcome.name}>
        <div style={{ maxWidth: "300px" }} className="truncate font-medium text-gray-900">
          {businessReportOutcome.name}
        </div>
        <div style={{ maxWidth: "300px" }} className="mt-2 text-sm text-gray-500">
          {businessReportOutcome.answer}
        </div>
      </td>
      <td
        className="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
        title={formatDateTime(businessReportOutcome.created_at)}
      >
        <span>{formatDateShort(businessReportOutcome.created_at)}</span>
        <span>·</span>
        <span>{formatTime(businessReportOutcome.created_at)}</span>
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        <div className="justify-content-start flex" style={{ width: 200 }}>
          {businessReportOutcome.status === "IN_PROGRESS" && (
            <div className="mr-1">
              <div className="flex items-center">
                <CircleLoader fill={colors.gray[500]} />
                <div className="text-xs">{normalizedProgressbar}%</div>
              </div>
            </div>
          )}
          <BusinessReportsOutcomeListRowStatus
            status={businessReportOutcome.status}
            errorMessage={businessReportOutcome.error_message}
          />
        </div>
      </td>
      <td className="px-3 py-4 text-sm">
        <SnapshotCollectionsOutcomesRowVerified businessReportOutcome={businessReportOutcome} />
      </td>
      <td className="whitespace-nowrap pl-3">
        <SnapshotCollectionsOutcomesRowActions businessReportOutcome={businessReportOutcome} />
      </td>
    </tr>
  );
};
